import { NgIf } from "@angular/common";
import { Component, OnDestroy, inject } from "@angular/core";
import { LucideDynamicIcon } from "@lucide/angular";
import { Subscription } from "rxjs";
import { AuthService, UserStreak } from "../core/auth.service";
import { LanguageService } from "../core/language.service";

const CELEBRATION_DURATION_MS = 3000;

@Component({
  selector: "app-streak-celebration",
  standalone: true,
  imports: [NgIf, LucideDynamicIcon],
  template: `
    <div
      *ngIf="visible"
      class="fixed inset-0 z-[120] flex items-center justify-center bg-foreground/35 p-4 backdrop-blur-sm"
    >
      <section
        role="status"
        aria-live="polite"
        class="streak-celebration-panel relative w-[min(34rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-secondary/70 bg-card p-7 text-center text-card-foreground shadow-2xl sm:p-10"
      >
        <div class="sunset-gradient absolute inset-x-0 top-0 h-2"></div>

        <svg
          aria-hidden="true"
          lucideIcon="sparkles"
          class="streak-celebration-sparkle absolute left-8 top-8 h-8 w-8 text-secondary"
        ></svg>
        <svg
          aria-hidden="true"
          lucideIcon="sparkles"
          class="streak-celebration-sparkle absolute bottom-8 right-8 h-9 w-9 text-accent"
          style="animation-delay: 180ms"
        ></svg>

        <div
          class="mx-auto mb-5 flex h-24 w-24 items-center justify-center rounded-full bg-secondary/20 text-destructive"
        >
          <svg
            aria-hidden="true"
            lucideIcon="flame"
            class="streak-celebration-flame h-14 w-14"
          ></svg>
        </div>

        <p
          class="text-xs font-semibold uppercase tracking-[0.24em] text-muted-foreground"
        >
          {{ language.t("streakCelebration.overline") }}
        </p>
        <h2
          class="mt-3 text-3xl font-bold tracking-normal text-foreground sm:text-4xl"
        >
          {{ language.t("streakCelebration.title") }}
        </h2>

        <div class="mt-6 flex items-end justify-center gap-3">
          <span
            class="text-7xl font-black leading-none text-primary sm:text-8xl"
          >
            {{ dayCount }}
          </span>
          <span class="pb-2 text-2xl font-bold text-foreground sm:text-3xl">
            {{ dayLabel() }}
          </span>
        </div>

        <p
          class="mx-auto mt-5 max-w-sm text-base text-muted-foreground sm:text-lg"
        >
          {{ language.t("streakCelebration.message") }}
        </p>
      </section>
    </div>
  `,
  styles: [
    `
      :host {
        display: contents;
      }

      .streak-celebration-panel {
        animation: streak-celebration-pop 420ms cubic-bezier(0.2, 0.9, 0.2, 1)
          both;
      }

      .streak-celebration-flame {
        animation: streak-celebration-flame 900ms ease-in-out infinite alternate;
        filter: drop-shadow(0 0 14px rgb(255 122 49 / 55%));
      }

      .streak-celebration-sparkle {
        animation: streak-celebration-sparkle 1.2s ease-in-out infinite;
      }

      @keyframes streak-celebration-pop {
        from {
          opacity: 0;
          transform: translateY(16px) scale(0.92);
        }

        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      @keyframes streak-celebration-flame {
        from {
          transform: translateY(2px) scale(0.94);
        }

        to {
          transform: translateY(-3px) scale(1.08);
        }
      }

      @keyframes streak-celebration-sparkle {
        0%,
        100% {
          opacity: 0.35;
          transform: scale(0.88) rotate(-8deg);
        }

        50% {
          opacity: 1;
          transform: scale(1.12) rotate(7deg);
        }
      }
    `,
  ],
})
export class StreakCelebrationComponent implements OnDestroy {
  readonly language = inject(LanguageService);
  private readonly auth = inject(AuthService);
  private readonly subscription: Subscription;
  private hideTimer?: number;

  visible = false;
  dayCount = 0;

  constructor() {
    this.subscription = this.auth.loginCelebration$.subscribe((streak) =>
      this.show(streak),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.hideTimer) window.clearTimeout(this.hideTimer);
  }

  dayLabel() {
    return this.language.t(
      this.dayCount === 1 ? "streakCelebration.day" : "streakCelebration.days",
    );
  }

  private show(streak: UserStreak) {
    this.dayCount = Math.max(0, Math.floor(Number(streak.current_streak) || 0));
    this.visible = true;

    if (this.hideTimer) window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      this.visible = false;
      this.hideTimer = undefined;
    }, CELEBRATION_DURATION_MS);
  }
}
